import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useContact } from '../../contexts/ContactContext';
import ConfirmDialog from './ui/ConfirmDialog';
import DataTable from './ui/DataTable';
import Modal from './ui/Modal';
import StatusBadge from './ui/StatusBadge';

const MessagesManager = () => {
  const { messages, loading, error, markAsRead, deleteMessage, getContactMessages } = useContact();
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('table');

  const parseDate = (dateValue) => {
    if (!dateValue) return new Date();
    
    // Handle Firestore Timestamp format
    if (dateValue._seconds && typeof dateValue._seconds === 'number') {
      // Convert Firestore timestamp to JavaScript Date
      // _seconds is Unix timestamp in seconds, _nanoseconds provides additional precision
      const milliseconds = dateValue._seconds * 1000 + Math.floor(dateValue._nanoseconds / 1000000);
      return new Date(milliseconds);
    }
    
    // Handle regular date strings or Date objects
    const parsed = new Date(dateValue);
    if (isNaN(parsed.getTime())) {
      console.warn('Invalid date value:', dateValue, 'Using current date instead');
      return new Date();
    }
    return parsed;
  };

  // Filter and sort messages
  const filteredAndSortedMessages = messages
    .filter(message => {
      const matchesSearch = 
        message.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        message.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        message.message.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || message.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'date':
          aValue = parseDate(a.updatedAt).getTime();
          bValue = parseDate(b.updatedAt).getTime();
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const columns = [
    { field: 'name', header: 'From', width: '20%' },
    { field: 'email', header: 'Email', width: '25%' },
    { 
      field: 'createdAt', 
      header: 'Date', 
      type: 'date', 
      width: '15%',
      render: (row) => {
        const date = parseDate(row.updatedAt);
        return date.toLocaleDateString();
      }
    },
    { 
      field: 'status', 
      header: 'Status', 
      width: '15%',
      render: (row) => <StatusBadge status={row.status} />
    },
    {
      field: 'actions',
      header: 'Actions',
      width: '25%',
      render: (row) => (
        <div className="flex space-x-2">
          <button 
            onClick={() => handleViewMessage(row)}
            className="btn btn-sm btn-outline hover:scale-105 transition-transform"
          >
            View
          </button>
          <button 
            onClick={() => handleDeleteClick(row)}
            className="btn btn-sm btn-error hover:scale-105 transition-transform"
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  useEffect(() => {
    getContactMessages();
  }, []);
  
  const handleDeleteClick = (message) => {
    setMessageToDelete(message);
    setIsDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      setProcessing(true);
      await deleteMessage(messageToDelete.id);
      toast.success('Message deleted successfully');
    } catch (error) {
      toast.error(`Failed to delete message: ${error.message}`);
    } finally {
      setProcessing(false);
      setIsDeleteConfirmOpen(false);
    }
  };

  const handleViewMessage = (message) => {
    if (message.status === 'new') {
      markAsRead(message.id);
    }
    setSelectedMessage(message);
  };

  const handleMarkAllAsRead = async () => {
    const unreadMessages = messages.filter(msg => msg.status === 'new');
    if (unreadMessages.length === 0) {
      toast.info('No unread messages');
      return;
    }
    
    try {
      await Promise.all(unreadMessages.map(msg => markAsRead(msg.id)));
      toast.success(`Marked ${unreadMessages.length} messages as read`);
    } catch (error) {
      toast.error('Failed to mark messages as read');
    }
  };

  const getMessageStats = () => {
    const total = messages.length;
    const newCount = messages.filter(msg => msg.status === 'new').length;
    const readCount = messages.filter(msg => msg.status === 'read').length;
    
    return { total, newCount, readCount };
  };

  const stats = getMessageStats();

  const MessageCard = ({ message }) => (
    <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden group">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
              {message.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{message.name}</h3>
              <p className="text-sm text-gray-500">{message.email}</p>
            </div>
          </div>
          <StatusBadge status={message.status} />
        </div>
        
        <div className="mb-4">
          <p className="text-gray-700 text-sm line-clamp-2">{message.message}</p>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <span>{parseDate(message.updatedAt).toLocaleDateString()}</span>
            {message.campus && <span>• {message.campus}</span>}
          </div>
          <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button 
              onClick={() => handleViewMessage(message)}
              className="px-3 py-1 text-xs bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 transition-colors"
            >
              View
            </button>
            <button 
              onClick={() => handleDeleteClick(message)}
              className="px-3 py-1 text-xs bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                Contact Messages
              </h1>
              <p className="text-gray-600 mt-1">Manage and respond to customer inquiries</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleMarkAllAsRead}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm hover:shadow-md"
                disabled={stats.newCount === 0}
              >
                Mark All Read ({stats.newCount})
              </button>
              <div className="flex bg-white rounded-lg shadow-sm border">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-4 py-2 rounded-l-lg transition-colors ${
                    viewMode === 'table' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Table
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`px-4 py-2 rounded-r-lg transition-colors ${
                    viewMode === 'cards' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Cards
                </button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Messages</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8l-4 4m0 0l-4-4m4 4V3" />
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">New Messages</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.newCount}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Read Messages</p>
                  <p className="text-2xl font-bold text-green-600">{stats.readCount}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            
            <div className="flex gap-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="read">Read</option>
              </select>
              
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="date-desc">Newest First</option>
                <option value="date-asc">Oldest First</option>
                <option value="name-asc">Name A-Z</option>
                <option value="name-desc">Name Z-A</option>
                <option value="status-asc">Status</option>
              </select>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-red-700">{error.message || 'Failed to load messages'}</span>
            </div>
          </div>
        )}

        {/* Content */}
        {viewMode === 'table' ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <DataTable
              data={filteredAndSortedMessages}
              columns={columns}
              loading={loading}
              emptyMessage="No messages found"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </div>
                </div>
              ))
            ) : filteredAndSortedMessages.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8l-4 4m0 0l-4-4m4 4V3" />
                </svg>
                <p className="text-gray-500">No messages found</p>
              </div>
            ) : (
              filteredAndSortedMessages.map((message) => (
                <MessageCard key={message.id} message={message} />
              ))
            )}
          </div>
        )}
      </div>
      
      {/* Enhanced Modal */}
      <Modal
        isOpen={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
        title={`Message from ${selectedMessage?.name}`}
        size="lg"
      >
        {selectedMessage && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                  {selectedMessage.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedMessage.name}</h2>
                  <p className="text-gray-600">{selectedMessage.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Contact Details</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Email:</span> {selectedMessage.email}</p>
                    <p><span className="font-medium">Phone:</span> {selectedMessage.phone || 'Not provided'}</p>
                    <p><span className="font-medium">Campus:</span> {selectedMessage.campus || 'Not specified'}</p>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Message Info</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Date:</span> {parseDate(selectedMessage.updatedAt).toLocaleDateString()}</p>
                    <p><span className="font-medium">Time:</span> {parseDate(selectedMessage.updatedAt).toLocaleTimeString()}</p>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">Status:</span>
                      <StatusBadge status={selectedMessage.status} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Message Content</h3>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-gray-700 whitespace-pre-line leading-relaxed">{selectedMessage.message}</p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={() => setSelectedMessage(null)}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  window.location.href = `mailto:${selectedMessage.email}`;
                }}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm hover:shadow-md"
              >
                Reply via Email
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete message from ${messageToDelete?.name}?`}
        confirmText="Delete"
        cancelText="Cancel"
        processing={processing}
      />
    </div>
  );
};

export default MessagesManager;